'use client'

import { Disclosure } from '@headlessui/react'
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/solid'
import { Fragment } from 'react'

import { useSeoStrucureData } from '@src/app/[country]/[lang]/[[...slug]]/SeoProvider'
import { filterBuilderAttributes } from '@src/utils/builder.util'
import { formatStringForSEO } from '@src/utils/seo.util'
import { cn } from '@src/utils/tailwind'

import { Container } from '../Container/Container'
import { defaultProps } from './Faq.defaults'

export interface FaqProps {
  items: Array<{
    question: string
    answer: string
  }>
  attributes?: any
  hideJsonLd?: boolean
}

export const Faq = (props: FaqProps = defaultProps) => {
  const { items = [], attributes, hideJsonLd } = props
  const { seoStructuredData } = useSeoStrucureData()
  const isFaqPopulated = seoStructuredData?.faq?.length > 1

  if (items.length === 0) return null

  const builderAttributes = filterBuilderAttributes(attributes)

  const seoTemplate = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: formatStringForSEO(item.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: formatStringForSEO(item.answer),
      },
    })),
  }

  const shouldRenderSchema =
    !hideJsonLd && !isFaqPopulated && (seoStructuredData?.generateFaqMarkup ?? true)

  return (
    <>
      <Container {...builderAttributes} className={cn(builderAttributes.className)}>
        <dl className="w-full space-y-4">
          {items.map((item) => (
            <Disclosure as="div" key={item.question} className="w-full text-bodi-gray-dark">
              {({ open }) => (
                <Fragment key={item.question}>
                  <dt>
                    <Disclosure.Button
                      className={cn(
                        'flex w-full items-center justify-between rounded-lg border border-bodi-gray-light bg-bodi-white p-6 text-left shadow-sm',
                        {
                          'rounded-b-none': open,
                        }
                      )}
                    >
                      <h4
                        className="text-body-md font-medium"
                        dangerouslySetInnerHTML={{ __html: item.question }}
                      />
                      <span className="ml-6 flex h-6 items-center">
                        {open ? (
                          <MinusSmallIcon className="size-6" aria-hidden="true" />
                        ) : (
                          <PlusSmallIcon className="size-6" aria-hidden="true" />
                        )}
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel
                    as="dd"
                    className="rounded-b-lg border border-t-0 border-bodi-gray-light bg-bodi-white p-6 shadow-sm"
                  >
                    <p
                      className="prose text-body-md"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </Disclosure.Panel>
                </Fragment>
              )}
            </Disclosure>
          ))}
        </dl>
      </Container>
      {shouldRenderSchema && (
        <script
          id="faq"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(seoTemplate, null, 2) }}
        />
      )}
    </>
  )
}
